import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
} from "react";
import { io, Socket } from "socket.io-client";

interface SocketContextType {
  socket: Socket | null;
  paymentSocket: Socket | null;
  connected: boolean;
  paymentConnected: boolean;
  userSecretKey: string;
  paymentSecretKey: string;
  subscribed: boolean;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  paymentSocket: null,
  connected: false,
  paymentConnected: false,
  userSecretKey: "",
  paymentSecretKey: "",
  subscribed: false,
});

export const useSocket = () => useContext(SocketContext);

interface Props {
  children: ReactNode;
  secretKey: string;
  paymentSecretKey: string;
}

const BASR_URL = (process.env.REACT_APP_BASE_URL_WS || "").trim();

/** Delay teardown so React StrictMode remount can reuse the live socket. */
const DISPOSE_DELAY_MS = 150;

export const SocketProvider = ({
  children,
  secretKey,
  paymentSecretKey,
}: Props) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [paymentSocket, setPaymentSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [paymentConnected, setPaymentConnected] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const paymentSocketRef = useRef<Socket | null>(null);
  const sprayDisposeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const paymentDisposeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    const trimmedSecret = secretKey.trim();

    if (sprayDisposeTimerRef.current) {
      clearTimeout(sprayDisposeTimerRef.current);
      sprayDisposeTimerRef.current = null;
    }

    if (!trimmedSecret) {
      return;
    }

    if (!socketRef.current) {
      if (!BASR_URL) {
        console.warn("Socket base URL is empty. Set REACT_APP_BASE_URL_WS.");
        return;
      }

      const sprayCansSocket = io(`${BASR_URL}/ws/spray-cans`, {
        query: { secretKey: trimmedSecret },
        transports: ["websocket"],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 10000,
        withCredentials: true,
      });

      socketRef.current = sprayCansSocket;
      setSocket(sprayCansSocket);

      sprayCansSocket.on("connect", () => {
        setConnected(true);
        sprayCansSocket.emit("subscribeToSprayCansStatus");
        setSubscribed(true);
      });

      sprayCansSocket.on("disconnect", () => {
        setConnected(false);
        setSubscribed(false);
      });

      sprayCansSocket.on("connect_error", (err) => {
        console.error("Spray-cans socket connect_error:", err?.message || err);
      });

      sprayCansSocket.on("error", (err) => {
        console.error("Spray-cans socket error:", err);
      });
    } else {
      setSocket(socketRef.current);
      setConnected(socketRef.current.connected);
    }

    return () => {
      sprayDisposeTimerRef.current = setTimeout(() => {
        if (socketRef.current) {
          socketRef.current.removeAllListeners();
          socketRef.current.disconnect();
          socketRef.current = null;
        }
        setSocket(null);
        setConnected(false);
        setSubscribed(false);
        sprayDisposeTimerRef.current = null;
      }, DISPOSE_DELAY_MS);
    };
  }, [secretKey]);

  // Ticket / payment socket — matches collage kiosk flow for ticketTimeUpdated
  useEffect(() => {
    const trimmedPaymentSecret = paymentSecretKey.trim();

    if (paymentDisposeTimerRef.current) {
      clearTimeout(paymentDisposeTimerRef.current);
      paymentDisposeTimerRef.current = null;
    }

    if (!trimmedPaymentSecret || !BASR_URL) {
      return;
    }

    if (!paymentSocketRef.current) {
      const payment = io(`${BASR_URL}/ws/payment`, {
        query: { secretKey: trimmedPaymentSecret },
        transports: ["websocket"],
        forceNew: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 10000,
        withCredentials: true,
      });

      paymentSocketRef.current = payment;
      setPaymentSocket(payment);

      payment.on("connect", () => {
        setPaymentConnected(true);
      });

      payment.on("disconnect", () => {
        setPaymentConnected(false);
      });

      payment.on("connect_error", (err) => {
        console.error("Payment socket connect_error:", err?.message || err);
        setPaymentConnected(false);
      });

      payment.on("error", (err) => {
        console.error("Payment socket error:", err);
      });
    } else {
      setPaymentSocket(paymentSocketRef.current);
      setPaymentConnected(paymentSocketRef.current.connected);
    }

    return () => {
      paymentDisposeTimerRef.current = setTimeout(() => {
        if (paymentSocketRef.current) {
          paymentSocketRef.current.removeAllListeners();
          paymentSocketRef.current.disconnect();
          paymentSocketRef.current = null;
        }
        setPaymentSocket(null);
        setPaymentConnected(false);
        paymentDisposeTimerRef.current = null;
      }, DISPOSE_DELAY_MS);
    };
  }, [paymentSecretKey]);

  return (
    <SocketContext.Provider
      value={{
        socket,
        paymentSocket,
        connected,
        paymentConnected,
        userSecretKey: secretKey,
        paymentSecretKey,
        subscribed,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
